/* eslint no-undef: 0 */ // --> OFF
/* @flow */
import { createAction } from "redux-actions";
import * as ActionTypes from "../../../actionTypes";
import WorkFlowController from "../../../services/workflowController";
import { userAccessTokenSelector, userRoleSelector } from "../../../security";
import { ADMIN_ROLE } from "../../../constants";

type Action = {
  +type: string,
  +payload?: any,
  +error?: boolean,
  +meta?: any
};

const workflowController = new WorkFlowController();

export const fetchAllAccountsRequested: Action = createAction(
  ActionTypes.FETCH_ALL_BILLING_ACCOUNTS_REQUESTED
);
export const fetchAllAccountsReceived: Action = createAction(
  ActionTypes.FETCH_ALL_BILLING_ACCOUNTS_RECEIVED
);
export const fetchAllAccountsFailed: Action = createAction(
  ActionTypes.FETCH_ALL_BILLING_ACCOUNTS_FAILED
);

const fetchAllAccountAPI = (createdBy, options) =>
  workflowController.findByFilter(
    "billingAccount",
    {
      where: {
        createdBy,
        deleted: false
      },
      order: ["locationId ASC", "name ASC"],
      paging: false
    },
    options
  );
const fetchAllAccountNoCreatedByAPI = options =>
  workflowController.findByFilter(
    "billingAccount",
    {
      where: { deleted: false },
      order: ["locationId ASC", "suite ASC"],
      paging: false
    },
    options
  );
export const fetchAllAccounts = () => async (dispatch, getState) => {
  dispatch(fetchAllAccountsRequested());
  try {
    const userRole = userRoleSelector(getState());
    const accounts =
      userRole === ADMIN_ROLE
        ? await fetchAllAccountNoCreatedByAPI({
            token: userAccessTokenSelector(getState())
          })
        : await fetchAllAccountAPI(userRole, {
            token: userAccessTokenSelector(getState())
          });
    dispatch(fetchAllAccountsReceived(accounts));
  } catch (err) {
    dispatch(fetchAllAccountsFailed(err.message));
  }
};

export const createAccountRequested: Action = createAction(
  ActionTypes.CREATE_BILLING_ACCOUNT_REQUESTED
);
export const createAccountReceived: Action = createAction(
  ActionTypes.CREATE_BILLING_ACCOUNT_RECEIVED
);
export const createAccountFailed: Action = createAction(
  ActionTypes.CREATE_BILLING_ACCOUNT_FAILED
);

const createAccountAPI = (
  name,
  contactEmails,
  rateCategory,
  address,
  meterIds,
  creditMeterIds,
  partialMeterIds,
  partialMeterPercentage,
  locationId,
  squareFootage,
  options
) =>
  workflowController.execute(
    "billingAccount",
    null,
    "create",
    {
      name,
      contactEmails,
      rateCategory,
      address,
      meterIds,
      creditMeterIds,
      partialMeterIds,
      partialMeterPercentage,
      locationId,
      squareFootage
    },
    options
  );
export const createAccount = (
  name,
  contactEmails,
  rateCategory,
  address,
  billableMeterIds,
  creditMeterIds,
  partialMeterIds,
  partialMeterPercentage,
  locationId,
  squareFootage
) => async (dispatch, getState) => {
  dispatch(createAccountRequested());
  try {
    await createAccountAPI(
      name,
      contactEmails,
      rateCategory,
      address,
      billableMeterIds,
      creditMeterIds,
      partialMeterIds,
      Number(partialMeterPercentage),
      locationId,
      squareFootage,
      { token: userAccessTokenSelector(getState()) }
    );
    dispatch(createAccountReceived());
    dispatch(fetchAllAccounts());
  } catch (err) {
    if (err.message[0].field === "partialMeterPercentage") {
      dispatch(
        createAccountFailed(
          "Please enter percent total cost for Partial Meters."
        )
      );
      return;
    }
    dispatch(createAccountFailed(err.message[0].msg));
  }
};

export const fetchAccountFilteredMetersRequested: Action = createAction(
  ActionTypes.FETCH_ACCOUNT_FILTERED_METERS_REQUESTED
);
export const fetchAccountFilteredMetersReceived: Action = createAction(
  ActionTypes.FETCH_ACCOUNT_FILTERED_METERS_RECEIVED
);
export const fetchAccountFilteredMetersFailed: Action = createAction(
  ActionTypes.FETCH_ACCOUNT_FILTERED_METERS_FAILED
);

const fetchFilteredBtuMetersAPI = (where, options) =>
  workflowController.findWhere("btuMeter", { where }, options);
const fetchFilteredMetersAPI = (where, options) =>
  workflowController.findWhere("meter", { where }, options);

function splitTrimAndRemoveEmpties(str, tok) {
  if (!str) return [];
  return str
    .split(tok)
    .map(x => x.trim())
    .filter(x => !!x);
}

export const fetchAccountFilteredMeters = filter => async (
  dispatch,
  getState
) => {
  dispatch(fetchAccountFilteredMetersRequested());
  try {
    const locationIds = splitTrimAndRemoveEmpties(filter.locationIds, ",");
    const groups = splitTrimAndRemoveEmpties(filter.groups, ",");
    const addressIds = splitTrimAndRemoveEmpties(filter.meterIds, ",");
    const where = { $and: [] };
    if (locationIds.length)
      where["$and"].push({ locationId: { $inq: locationIds } });
    if (groups.length) where["$and"].push({ group: { $inq: groups } });
    if (addressIds.length)
      where["$and"].push({ addressId: { $inq: addressIds } });
    if (where["$and"].length === 0) return;

    const meters = await fetchFilteredBtuMetersAPI(where, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(fetchAccountFilteredMetersReceived(meters));
  } catch (err) {
    dispatch(fetchAccountFilteredMetersFailed(err.message));
  }
};

export const fetchAccountRequested: Action = createAction(
  ActionTypes.FETCH_BILLING_ACCOUNT_REQUESTED
);
export const fetchAccountReceived: Action = createAction(
  ActionTypes.FETCH_BILLING_ACCOUNT_RECEIVED
);
export const fetchAccountFailed: Action = createAction(
  ActionTypes.FETCH_BILLING_ACCOUNT_FAILED
);

const fetchAccountAPICall = (createdBy, billingAccountId, options) =>
  workflowController.findWhere(
    "billingAccount",
    {
      createdBy,
      billingAccountId,
      deleted: false
    },
    options
  );
const fetchAccountNoCreatedByAPICall = (billingAccountId, options) =>
  workflowController.findWhere(
    "billingAccount",
    {
      billingAccountId,
      deleted: false
    },
    options
  );
export const fetchAccount = billingAccountId => async (dispatch, getState) => {
  dispatch(fetchAccountRequested());
  try {
    const userRole = userRoleSelector(getState());
    const results =
      userRole === ADMIN_ROLE
        ? await fetchAccountNoCreatedByAPICall(billingAccountId, {
            token: userAccessTokenSelector(getState())
          })
        : await fetchAccountAPICall(userRole, billingAccountId, {
            token: userAccessTokenSelector(getState())
          });
    dispatch(fetchAccountReceived(results.length ? results[0] : null));
  } catch (err) {
    dispatch(fetchAccountFailed(err.message));
  }
};

export const setAccountInfo: Action = createAction(
  ActionTypes.SET_ACCOUNT_INFO
);
export const unsetAccountInfo: Action = createAction(
  ActionTypes.UNSET_ACCOUNT_INFO
);

export const addLocationFilter: Action = createAction(
  ActionTypes.ADD_LOCATION_FILTER
);
export const addGroupFilter: Action = createAction(
  ActionTypes.ADD_GROUP_FILTER
);

export const addBillableLocationFilter: Action = createAction(
  ActionTypes.ADD_BILLABLE_LOCATION_FILTER
);
export const addBillableGroupFilter: Action = createAction(
  ActionTypes.ADD_BILLABLE_GROUP_FILTER
);
export const addBillableMeterFilter: Action = createAction(
  ActionTypes.ADD_BILLABLE_METER_FILTER
);

export const setCreditableMeters: Action = createAction(
  ActionTypes.SET_CREDITABLE_METERS
);
export const setBillableMeters: Action = createAction(
  ActionTypes.SET_BILLABLE_METERS
);

const buildQuery = (locationIds, groups, addressIds) => {
  const where = { $and: [] };
  if (locationIds.length)
    where["$and"].push({ locationId: { $inq: locationIds } });
  if (groups.length) where["$and"].push({ group: { $inq: groups } });
  if (addressIds.length)
    where["$and"].push({ addressId: { $inq: addressIds } });
  return where;
};

export const fetchAccountBillableMeters = (
  locationIds,
  groups,
  addressIds
) => async (dispatch, getState) => {
  dispatch(fetchAccountFilteredMetersRequested());
  try {
    const where = buildQuery(locationIds, groups, addressIds);
    if (where["$and"].length === 0) return;
    const meters = await fetchFilteredMetersAPI(where, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(setBillableMeters(meters));
  } catch (err) {
    dispatch(fetchAccountFilteredMetersFailed(err.message));
  }
};
const fetchLocationAccountFilteredBtuMetersAPI = (where, options) =>
  workflowController.findWhere("meter", { where }, options);
export const fetchLocationAccountBillableMeters = (
  locationIds,
  groups,
  addressIds
) => async (dispatch, getState) => {
  dispatch(fetchAccountFilteredMetersRequested());
  try {
    const where = buildQuery(locationIds, groups, addressIds);
    if (where["$and"].length === 0) return;
    const meters = await fetchLocationAccountFilteredBtuMetersAPI(where, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(setBillableMeters(meters));
  } catch (err) {
    dispatch(fetchAccountFilteredMetersFailed(err.message));
  }
};

export const addCreditableLocationFilter: Action = createAction(
  ActionTypes.ADD_CREDITABLE_LOCATION_FILTER
);
export const addCreditableGroupFilter: Action = createAction(
  ActionTypes.ADD_CREDITABLE_GROUP_FILTER
);
export const addCreditableMeterFilter: Action = createAction(
  ActionTypes.ADD_CREDITABLE_METER_FILTER
);

const fetchAccountFilteredElectricalMetersAPI = (where, options) =>
  workflowController.findWhere("electricalMeter", { where }, options);
export const fetchAccountCreditableMeters = (
  locationIds,
  groups,
  addressIds
) => async (dispatch, getState) => {
  dispatch(fetchAccountFilteredMetersRequested());
  try {
    const where = buildQuery(locationIds, groups, addressIds);
    if (where["$and"].length === 0) return;
    const meters = await fetchAccountFilteredElectricalMetersAPI(where, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(setCreditableMeters(meters));
  } catch (err) {
    dispatch(fetchAccountFilteredMetersFailed(err.message));
  }
};

export const addPartialLocationFilter: Action = createAction(
  ActionTypes.ADD_PARTIAL_LOCATION_FILTER
);
export const addPartialGroupFilter: Action = createAction(
  ActionTypes.ADD_PARTIAL_GROUP_FILTER
);
export const addPartialMeterFilter: Action = createAction(
  ActionTypes.ADD_PARTIAL_METER_FILTER
);
export const setPartialMeters: Action = createAction(
  ActionTypes.SET_PARTIAL_METERS
);
export const setPartialPercentage: Action = createAction(
  ActionTypes.SET_PARTIAL_METER_PERCENTAGE
);

export const fetchAccountPartialMeters = (
  locationIds,
  groups,
  addressIds
) => async (dispatch, getState) => {
  dispatch(fetchAccountFilteredMetersRequested());
  try {
    const where = buildQuery(locationIds, groups, addressIds);
    if (where["$and"].length === 0) return;
    const meters = await fetchFilteredBtuMetersAPI(where, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(setPartialMeters(meters));
  } catch (err) {
    dispatch(fetchAccountFilteredMetersFailed(err.message));
  }
};

export const fetchLocationAccountPartialMeters = (
  locationIds,
  groups,
  addressIds
) => async (dispatch, getState) => {
  dispatch(fetchAccountFilteredMetersRequested());
  try {
    const where = buildQuery(locationIds, groups, addressIds);
    if (where["$and"].length === 0) return;
    const meters = await fetchFilteredMetersAPI(where, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(setPartialMeters(meters));
  } catch (err) {
    dispatch(fetchAccountFilteredMetersFailed(err.message));
  }
};

export const resetBillableMeters: Action = createAction(
  ActionTypes.RESET_BILLABLE_METERS
);
export const resetCreditableMeters: Action = createAction(
  ActionTypes.RESET_CREDITABLE_METERS
);
export const resetPartialMeters: Action = createAction(
  ActionTypes.RESET_PARTIAL_METERS
);

export const deleteAccountRequested: Action = createAction(
  ActionTypes.DELETE_BILLING_ACCOUNT_REQUESTED
);
export const deleteAccountReceived: Action = createAction(
  ActionTypes.DELETE_BILLING_ACCOUNT_RECEIVED
);
export const deleteAccountFailed: Action = createAction(
  ActionTypes.DELETE_BILLING_ACCOUNT_FAILED
);

const deleteAccountAPI = (billingAccountId, options) =>
  workflowController.execute(
    "billingAccount",
    null,
    "delete",
    { billingAccountId },
    options
  );
export const deleteAccount = billingAccountId => async (dispatch, getState) => {
  dispatch(deleteAccountRequested());
  try {
    await deleteAccountAPI(billingAccountId, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(deleteAccountReceived());
    return true;
  } catch (err) {
    dispatch(deleteAccountFailed(err.message[0].msg));
    return false;
  }
};

export const fetchAccountDetailsAPI = (billingAccountId, options) =>
  workflowController.findOne(
    "billingAccountDetails",
    {
      billingAccountId,
      deleted: false
    },
    options
  );

export const addMeterToBillingAccountRequested: Action = createAction(
  ActionTypes.ADD_METER_TO_BILLING_ACCOUNT_REQUESTED
);
export const addMeterToBillingAccountReceived: Action = createAction(
  ActionTypes.ADD_METER_TO_BILLING_ACCOUNT_RECEIVED
);
export const addMeterToBillingAccountFailed: Action = createAction(
  ActionTypes.ADD_METER_TO_BILLING_ACCOUNT_FAILED
);

const addMeterToBillingAccountAPI = (data, options) =>
  workflowController.execute("billingAccount", null, "addmeter", data, options);
export const addMeterToBillingAccount = (
  billingAccountId,
  meterId,
  billingType
) => async (dispatch, getState) => {
  dispatch(addMeterToBillingAccountRequested());
  try {
    await addMeterToBillingAccountAPI(
      {
        billingAccountId,
        meterId,
        billingType
      },
      { token: userAccessTokenSelector(getState()) }
    );
    dispatch(addMeterToBillingAccountReceived());
    dispatch(fetchAllAccounts());
  } catch (err) {
    dispatch(addMeterToBillingAccountFailed(err.message[0].msg));
  }
};

export const removeMeterFromBillingAccountRequested: Action = createAction(
  ActionTypes.REMOVE_METER_FROM_BILLING_ACCOUNT_REQUESTED
);
export const removeMeterFromBillingAccountReceived: Action = createAction(
  ActionTypes.REMOVE_METER_FROM_BILLING_ACCOUNT_RECEIVED
);
export const removeMeterFromBillingAccountFailed: Action = createAction(
  ActionTypes.REMOVE_METER_FROM_BILLING_ACCOUNT_FAILED
);

const removeMeterFromBillingAccountAPI = (data, options) =>
  workflowController.execute(
    "billingAccount",
    null,
    "removemeter",
    data,
    options
  );
export const removeMeterFromBillingAccount = (
  billingAccountId,
  meterId,
  billingType
) => async (dispatch, getState) => {
  dispatch(removeMeterFromBillingAccountRequested());
  try {
    await removeMeterFromBillingAccountAPI(
      {
        billingAccountId,
        meterId,
        billingType
      },
      { token: userAccessTokenSelector(getState()) }
    );
    dispatch(removeMeterFromBillingAccountReceived());
    return true;
  } catch (err) {
    dispatch(removeMeterFromBillingAccountFailed(err.message[0].msg));
    return false;
  }
};

export const unsetAccountError: Action = createAction(
  ActionTypes.UNSET_ACCOUNT_ERROR
);

export const configurePartialMeterPercentageRequested: Action = createAction(
  ActionTypes.CONFIGURE_BILLING_ACCOUNT_PARTIAL_METER_PERCENTAGE_REQUESTED
);
export const configurePartialMeterPercentageReceived: Action = createAction(
  ActionTypes.CONFIGURE_BILLING_ACCOUNT_PARTIAL_METER_PERCENTAGE_RECEIVED
);
export const configurePartialMeterPercentageFailed: Action = createAction(
  ActionTypes.CONFIGURE_BILLING_ACCOUNT_PARTIAL_METER_PERCENTAGE_FAILED
);

const configurePartialMeterPercentageAPI = (data, options) =>
  workflowController.execute(
    "billingAccount",
    null,
    "configurepartialmeterpercentage",
    data,
    options
  );
export const configurePartialMeterPercentage = (
  billingAccountId,
  partialMeterPercentage
) => async (dispatch, getState) => {
  dispatch(configurePartialMeterPercentageRequested());
  try {
    await configurePartialMeterPercentageAPI(
      {
        billingAccountId,
        partialMeterPercentage: parseFloat(partialMeterPercentage)
      },
      { token: userAccessTokenSelector(getState()) }
    );
    dispatch(configurePartialMeterPercentageReceived());
  } catch (err) {
    dispatch(configurePartialMeterPercentageFailed(err.message[0].msg));
  }
};

const fetchBillingAccountMeterAPI = (meterId, createdBy, options) =>
  workflowController.findOne(
    "billingAccountMeter",
    {
      meterId,
      createdBy
    },
    options
  );

export const fetchBillingAccountMeter = (meterId, createdBy) => async (
  dispatch,
  getState
) => {
  return await fetchBillingAccountMeterAPI(meterId, createdBy, {
    token: userAccessTokenSelector(getState())
  });
};

export const editPartialMeterPercentageRequested: Action = createAction(
  ActionTypes.EDIT_BILLING_ACCOUNT_PARTIAL_METER_PERCENTAGE_REQUESTED
);
export const editPartialMeterPercentageReceived: Action = createAction(
  ActionTypes.EDIT_BILLING_ACCOUNT_PARTIAL_METER_PERCENTAGE_RECEIVED
);
export const editPartialMeterPercentageFailed: Action = createAction(
  ActionTypes.EDIT_BILLING_ACCOUNT_PARTIAL_METER_PERCENTAGE_FAILED
);

const editPartialMeterPercentageAPI = (data, options) =>
  workflowController.execute(
    "billingAccount",
    null,
    "editpartialmeterpercentage",
    data,
    options
  );
export const editPartialMeterPercentage = ({
  billingAccountId,
  meterId,
  partialMeterPercentage
}) => async (dispatch, getState) => {
  dispatch(editPartialMeterPercentageRequested());
  try {
    await editPartialMeterPercentageAPI(
      {
        billingAccountId,
        meterId,
        partialMeterPercentage: parseFloat(partialMeterPercentage)
      },
      { token: userAccessTokenSelector(getState()) }
    );
    // dispatch(editPartialMeterPercentageReceived())
    return true;
  } catch (err) {
    console.error(err);
    // dispatch(editPartialMeterPercentageFailed(`Problem editing partial meter percentage.`))
    return false;
  }
};

export const addContactToBillingAccount = (
  billingAccountId,
  firstName,
  lastName,
  email
) => async (dispatch, getState) => {
  try {
    await workflowController.execute(
      "billingAccount",
      billingAccountId,
      "addContact",
      { email, firstName, lastName },
      {
        token: userAccessTokenSelector(getState())
      }
    );
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
};

const removeEmailFromBillingAccountAPI = (billingAccountId, email, options) =>
  workflowController.execute(
    "billingAccount",
    billingAccountId,
    "removeEmail",
    { email },
    options
  );
export const removeEmailFromBillingAccount = (
  billingAccountId,
  email
) => async (dispatch, getState) => {
  try {
    await removeEmailFromBillingAccountAPI(billingAccountId, email, {
      token: userAccessTokenSelector(getState())
    });
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const setBillingAccountSquareFootage = (
  billingAccountId,
  squareFootage
) => async (dispatch, getState) => {
  try {
    await workflowController.execute(
      "billingAccount",
      billingAccountId,
      "setSquareFootage",
      { squareFootage },
      {
        token: userAccessTokenSelector(getState())
      }
    );
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const editBillingAccount = (
  billingAccountId,
  name,
  rateCategory,
  address,
  squareFootage
) => async (dispatch, getState) => {
  try {
    await workflowController.execute(
      "billingAccount",
      billingAccountId,
      "edit",
      {
        name,
        rateCategory,
        address,
        squareFootage
      },
      {
        token: userAccessTokenSelector(getState())
      }
    );
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const editContact = (contactId, firstName, lastName, email) => async (
  dispatch,
  getState
) => {
  try {
    await workflowController.execute(
      "contact",
      null,
      "update",
      { contactId, email, firstName, lastName },
      { token: userAccessTokenSelector(getState()) }
    );
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const setGridSortAction: Action = createAction(
  ActionTypes.ACCOUNT_SET_GRID_SORT
);
export const setGridSort = (sortColumn, sortDirection) => dispatch => {
  dispatch({
    type: ActionTypes.ACCOUNT_SET_GRID_SORT,
    payload: {
      sortColumn,
      sortDirection
    }
  });
};

export const setGridFiltersAction: Action = createAction(
  ActionTypes.ACCOUNT_SET_GRID_FILTERS
);
export const setGridFilters = filters => dispatch => {
  dispatch({
    type: ActionTypes.ACCOUNT_SET_GRID_FILTERS,
    payload: {
      filters
    }
  });
};



// WEBPACK FOOTER //
// ./src/features/account/store/accountActions.js